import { moment } from '@pbt/pbt-ui-components'

export interface OpeningHours {
  __typename?: string
  dayOfWeek?: string | null
  workingHours?: {
    __typename?: string
    from: any
    to: any
  } | null
}

interface CalculatedDueTime {
  dueDate: string
  dueTime: string
}

const DEFAULT_FALLBACK_OPEN_TIME = '10:00'
const DEFAULT_FALLBACK_CLOSE_TIME = '18:00'
const HOURS_AHEAD = 2

/**
 * Day of week map to convert from GraphQL response format to moment.js day index
 * GraphQL returns: MONDAY, TUESDAY, etc.
 * moment.js uses: 0 = Sunday, 1 = Monday, etc.
 */
const DAY_OF_WEEK_MAP: Record<string, number> = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
}

/**
 * Extracts time (HH:mm) from an ISO datetime string in a given timezone
 */
const extractTimeFromISO = (isoString: string, timezone: string): string =>
  moment.tz(isoString, timezone).format('HH:mm')

/**
 * Finds the opening hours for a specific day of week
 */
const findOpeningHoursForDay = (
  openingHours: OpeningHours[] | null | undefined,
  dayOfWeek: number,
): OpeningHours | null => {
  if (!openingHours || openingHours.length === 0) {
    return null
  }

  const dayName = Object.keys(DAY_OF_WEEK_MAP).find(
    (key) => DAY_OF_WEEK_MAP[key] === dayOfWeek,
  )

  if (!dayName) {
    return null
  }

  return (
    openingHours.find(
      (hours) =>
        hours?.dayOfWeek === dayName &&
        hours?.workingHours?.from &&
        hours?.workingHours?.to,
    ) || null
  )
}

/**
 * Finds the next working day starting from a given day
 * Returns null if no working days are configured in the next 7 days
 */
const findNextWorkingDay = (
  openingHours: OpeningHours[] | null | undefined,
  startDayOfWeek: number,
): { dayOfWeek: number; hours: OpeningHours } | null => {
  if (!openingHours || openingHours.length === 0) {
    return null
  }

  // Check up to 7 days ahead
  for (let daysAhead = 1; daysAhead <= 7; daysAhead++) {
    const nextDay = (startDayOfWeek + daysAhead) % 7
    const hours = findOpeningHoursForDay(openingHours, nextDay)

    if (hours) {
      return { dayOfWeek: nextDay, hours }
    }
  }

  return null
}

/**
 * Helper function to calculate due time for next working day
 */
const getNextWorkingDayDueTime = (
  now: moment.Moment,
  nextWorkingDay: { dayOfWeek: number; hours: OpeningHours },
  timezone: string,
  currentDayOfWeek: number,
): CalculatedDueTime => {
  const openingTime = extractTimeFromISO(
    nextWorkingDay.hours.workingHours!.from,
    timezone,
  )
  const daysToAdd =
    nextWorkingDay.dayOfWeek > currentDayOfWeek
      ? nextWorkingDay.dayOfWeek - currentDayOfWeek
      : 7 - currentDayOfWeek + nextWorkingDay.dayOfWeek

  const nextDayOpening = now
    .clone()
    .add(daysToAdd, 'days')
    .set({
      hour: Number.parseInt(openingTime.split(':')[0], 10),
      minute: Number.parseInt(openingTime.split(':')[1], 10),
      second: 0,
      millisecond: 0,
    })
    .add(HOURS_AHEAD, 'hours')

  return {
    dueDate: nextDayOpening.toISOString(),
    dueTime: nextDayOpening.toISOString(),
  }
}

/**
 * Calculates the default due date and time for a new task based on business hours
 *
 * Rules:
 * 1. If current time is on a working day:
 *    a. If before opening time: use opening time + 2h
 *    b. If during working hours: use current time + 2h (if still before closing), else next working day
 * 2. If not on a working day: find next working day and use opening time + 2h
 * 3. If no business hours configured: use fallback 6 PM / 10 AM logic
 *
 * @param creationTime - ISO string of when the task creation UI opened (in business timezone)
 * @param openingHours - Array of opening hours from GraphQL query
 * @param timezone - Business timezone (e.g., 'America/New_York')
 * @returns Object with dueDate and dueTime as ISO strings in business timezone
 */
export const calculateDefaultDueTime = (
  creationTime: string,
  openingHours: OpeningHours[] | null | undefined,
  timezone: string,
): CalculatedDueTime => {
  // Parse creation time in business timezone
  const now = moment.tz(creationTime, timezone)
  const currentDayOfWeek = now.day()
  const todayHours = findOpeningHoursForDay(openingHours, currentDayOfWeek)

  // Case 1: Today is a working day with hours configured
  if (todayHours?.workingHours) {
    const openingTime = extractTimeFromISO(
      todayHours.workingHours.from,
      timezone,
    )
    const closingTime = extractTimeFromISO(todayHours.workingHours.to, timezone)

    const openingMoment = now.clone().set({
      hour: Number.parseInt(openingTime.split(':')[0], 10),
      minute: Number.parseInt(openingTime.split(':')[1], 10),
      second: 0,
      millisecond: 0,
    })

    const closingMoment = now.clone().set({
      hour: Number.parseInt(closingTime.split(':')[0], 10),
      minute: Number.parseInt(closingTime.split(':')[1], 10),
      second: 0,
      millisecond: 0,
    })

    // If current time is before opening, use opening + 2h
    if (now.isBefore(openingMoment)) {
      const dueTime = openingMoment.clone().add(HOURS_AHEAD, 'hours')

      // Check if opening + 2h is still before closing
      if (dueTime.isSameOrBefore(closingMoment)) {
        return {
          dueDate: dueTime.toISOString(),
          dueTime: dueTime.toISOString(),
        }
      }
      // Opening + 2h is after closing, go to next working day
      const nextWorkingDay = findNextWorkingDay(openingHours, currentDayOfWeek)
      if (nextWorkingDay?.hours.workingHours) {
        return getNextWorkingDayDueTime(
          now,
          nextWorkingDay,
          timezone,
          currentDayOfWeek,
        )
      }
    }

    // Current time is during or after working hours
    const defaultDueTime = now.clone().add(HOURS_AHEAD, 'hours')

    // If +2h is before or at closing time, use it
    if (defaultDueTime.isSameOrBefore(closingMoment)) {
      return {
        dueDate: defaultDueTime.toISOString(),
        dueTime: defaultDueTime.toISOString(),
      }
    }

    // +2h is after closing time - find next working day
    const nextWorkingDay = findNextWorkingDay(openingHours, currentDayOfWeek)
    if (nextWorkingDay?.hours.workingHours) {
      return getNextWorkingDayDueTime(
        now,
        nextWorkingDay,
        timezone,
        currentDayOfWeek,
      )
    }
  }

  // Case 2: Not a working day - find next working day
  if (openingHours && openingHours.length > 0) {
    const nextWorkingDay = findNextWorkingDay(openingHours, currentDayOfWeek)
    if (nextWorkingDay?.hours.workingHours) {
      return getNextWorkingDayDueTime(
        now,
        nextWorkingDay,
        timezone,
        currentDayOfWeek,
      )
    }
  }

  // Case 3: No business hours configured - use fallback logic
  const defaultDueTime = now.clone().add(HOURS_AHEAD, 'hours')
  const fallbackClosingTime = DEFAULT_FALLBACK_CLOSE_TIME
  const fallbackClosingMoment = now.clone().set({
    hour: Number.parseInt(fallbackClosingTime.split(':')[0], 10),
    minute: Number.parseInt(fallbackClosingTime.split(':')[1], 10),
    second: 0,
    millisecond: 0,
  })

  // If +2h is before or at 6 PM, use it
  if (defaultDueTime.isSameOrBefore(fallbackClosingMoment)) {
    return {
      dueDate: defaultDueTime.toISOString(),
      dueTime: defaultDueTime.toISOString(),
    }
  }

  // +2h is after 6 PM - schedule for next day at 10 AM + 2h (12 PM)
  const fallbackOpeningTime = DEFAULT_FALLBACK_OPEN_TIME
  const nextDayFallback = now
    .clone()
    .add(1, 'day')
    .set({
      hour: Number.parseInt(fallbackOpeningTime.split(':')[0], 10),
      minute: Number.parseInt(fallbackOpeningTime.split(':')[1], 10),
      second: 0,
      millisecond: 0,
    })
    .add(HOURS_AHEAD, 'hours')

  return {
    dueDate: nextDayFallback.toISOString(),
    dueTime: nextDayFallback.toISOString(),
  }
}
